import React from "react";

// Print-faithful postcard renderer. MASTER_SPEC Draft80 §10.10, LUX-10, BRAND-01.
// 6" x 4" trim (6.25" x 4.25" with bleed), rendered at the trim ratio so it
// scales to its container and matches what arrives in the mail.
//
// What this draws changed under Draft80, in two ways that both mattered:
//
//   The front printed "Carecard · Express" and the facility's name across the
//   bottom. Neither belongs on a mailed card. §10.10.1 makes the front a
//   full-bleed customer photograph and nothing else, and BRAND-01 forbids
//   putting the brand on printable artwork for marketing. A preview that shows
//   branding the printer will not print is also just wrong.
//
//   The back printed a return address in the top-left corner. That corner
//   is the provider's to stamp, not CareCard's to draw (§10.10.2): since
//   the owner decision of 2026-10-29 the provider stamps the sender's own
//   return address there from the payload's From block (B-02, §20.24
//   BUILD-24). BSO has still published no rule about what an outside
//   commercial sender must print there, and none is claimed.
//
// The recipient block is still shown, because a sender checking their own
// card needs to see where it is going — but it is labelled as the mail
// partner's, which is the distinction §10.10.2 exists to draw.

// A sample photograph standing in for the one a customer would upload.
//
// This is an AI-generated sample photograph of a fictional family — no real
// people, so no stock licensing and nobody's actual family on this page. It
// replaced the drawn sunset-pier illustration on owner direction 2026-10-15:
// the card face should show a photograph, the way a real sent card looks.
// The asset is 3:2, the finished card's ratio, and object-fit cover gives it
// the same full-bleed fill the illustration had.
function PhotoPlaceholder({ priority = false } = {}) {
  return (
    <picture style={{ display: "block", width: "100%", height: "100%" }}>
      <source type="image/webp"
        srcSet="/brand/sample-family-photo-360.webp 360w, /brand/sample-family-photo-720.webp 720w, /brand/sample-family-photo-900.webp 900w"
        sizes="(max-width: 760px) 300px, 480px" />
    <img
      src="/brand/sample-family-photo.jpg"
      width={900}
      height={600}
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : "auto"}
      decoding="async"
      alt=""
      style={{ width: "100%", height: "100%", display: "block", objectFit: "cover" }}
    />
    </picture>
  );
}

function Postcard({ side = "front", imageUrl, photoOverride, facility, recipient, message, scale = 1 }) {
  if (side === "front") {
    return (
      <div className="postcard">
        <div className="pc-front grain">
          <div className="photo-frame">
            {photoOverride
              ? photoOverride
              : imageUrl
                ? <img src={imageUrl} alt=""/>
                : <PhotoPlaceholder/>}
          </div>
        </div>
      </div>
    );
  }

  // BACK
  return (
    <div className="postcard">
      <div className="pc-back grain">
        <div className="divider"/>
        <div className="msg">
          {/* The one region CareCard prints. The return-address area is the
              provider's to stamp: see the note at the top of this file (B-02). */}
          <div className="text">{message || "Your message will appear here."}</div>
        </div>
        <div className="addr-side">
          <div style={{display:'flex', justifyContent:'flex-end', alignItems:'flex-start'}}>
            <div className="stamp" style={{
              border: '1px dashed rgba(0, 0, 0, 0.45)',
              background: 'transparent',
              color: 'rgba(0, 0, 0, 0.65)',
              fontSize: 7, lineHeight: 1.15, letterSpacing: 0,
              fontStyle: 'italic',
            }}>
              <div style={{fontWeight:600, textTransform:'uppercase', fontStyle:'normal', letterSpacing:'0.05em'}}>Postage</div>
              <div style={{marginTop:2}}>applied by<br/>printer</div>
            </div>
          </div>
          <div className="to">
            <div className="lbl">To · added by the mail partner</div>
            <span className="name">{recipient?.name || "Recipient name"}</span> / {recipient?.arrestNumber || "Arrest #"}<br/>
            {recipient?.housing ? <>{recipient.housing}<br/></> : null}
            {facility?.name || "Facility Name"}<br/>
            {facility?.address_line_2 || "PO Box"}<br/>
            {facility?.city || "City"}, {facility?.state || "FL"} {facility?.zip || "00000"}
          </div>
          <div className="imb"></div>
        </div>
      </div>
    </div>
  );
}

export { Postcard, PhotoPlaceholder };
